import { Button, Input, Link, Text, VStack } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import useTranslate from "../lng/useTranslate"
import KeyboardFormSubmit from "./state/KeyboardFormSubmit"

// By default, we parse out the first word (basically), requiring user to type only
// this portion of the confirm value.
function defaultParseConfirmValue(value: string) {
  return value.trim().match(/\S+/)?.[0] || value
}

type ConfirmDangerousActionFormProps = {
  confirmValue: string
  parseConfirmValue?: (value: string) => string
  tConfirmLabel: (props: { label: string }) => string
  onSubmit: () => void
  onCancel: () => void
  children: ({ formAction }: { formAction: React.ReactNode }) => React.ReactNode
}

function ConfirmDangerousActionForm({
  confirmValue,
  parseConfirmValue = defaultParseConfirmValue,
  tConfirmLabel,
  onSubmit,
  onCancel,
  children
}: ConfirmDangerousActionFormProps) {
  const { t } = useTranslate()
  const parsedConfirmValue = parseConfirmValue(confirmValue)

  return (
    <Formik
      initialValues={{ confirm: "" }}
      validate={(values) => {
        if (values.confirm !== parsedConfirmValue)
          return { confirm: "required" }
      }}
      validateOnMount
      onSubmit={onSubmit}
    >
      {(form) =>
        children({
          formAction: (
            <Form style={{ width: "100%" }}>
              <KeyboardFormSubmit />

              <VStack w="100%" gap={4}>
                <Field
                  as={Input}
                  name="confirm"
                  placeholder={t(
                    `confirm_dangerous_action.input.Type "{{ label }}" to confirm`,
                    {
                      label: parsedConfirmValue
                    }
                  )}
                  autoComplete="off"
                  mt={4}
                  mb="0 !important"
                  w="100%"
                  autoFocus
                />

                <Button
                  type="submit"
                  size="lg"
                  colorPalette="red"
                  disabled={!form.isValid}
                  loading={form.isSubmitting}
                >
                  {/* Using lineClamp on the nested span instead of on the button since the extra
                  text is otherwise visible on the second line of the button text. */}
                  <Text
                    as="span"
                    lineClamp={1}
                    textAlign="center"
                    color="inherit"
                    fontWeight="inherit"
                  >
                    {tConfirmLabel({ label: confirmValue })}
                  </Text>
                </Button>

                <Link variant="underline" onClick={onCancel}>
                  {t("confirm_dangerous_action.cancel.Nevermind, go back")}
                </Link>
              </VStack>
            </Form>
          )
        })
      }
    </Formik>
  )
}

export default ConfirmDangerousActionForm
